/**
 * Single source of truth for Trust Center signals.
 * Update statuses, target dates, and evidence URLs here when audits land.
 */

export type TrustStatus = "verified" | "in-progress" | "aligned";

export interface TrustSignal {
  id: string;
  /** i18n key under `trustCenter.signals.{id}.label` */
  labelKey: string;
  /** i18n key for the supporting one-liner */
  descKey: string;
  status: TrustStatus;
  /** Optional target date for in-progress items (free text, e.g. "Q3 2026") */
  targetDate?: string;
  /** Optional evidence/report link (mailto, PDF, or external) */
  evidenceUrl?: string;
  /** Lucide icon name string — resolved at render time */
  icon: "shield-check" | "lock" | "server" | "globe" | "clock" | "file-check" | "key";
  /** Optional badge artwork path (served from /badges). Renders in variant="logo". */
  badgeImage?: string;
}

// Tier 1 — verified, factual today
export const tier1Signals: TrustSignal[] = [
  {
    id: "gdpr",
    labelKey: "trustCenter.signals.gdpr.label",
    descKey: "trustCenter.signals.gdpr.desc",
    // Not "verified": GDPR has no certification to hold, so a Verified chip
    // beside it would claim something that does not exist. The specifics live
    // in the description.
    status: "aligned",
    icon: "shield-check",
    badgeImage: "/badges/gdpr.svg",
  },
];

// Tier 2 — audits underway, honest target dates
export const tier2Signals: TrustSignal[] = [
  {
    id: "soc2",
    labelKey: "trustCenter.signals.soc2.label",
    descKey: "trustCenter.signals.soc2.desc",
    status: "in-progress",
    targetDate: "Q3 2026",
    icon: "file-check",
    badgeImage: "/badges/soc2.svg",
  },
  {
    id: "iso27001",
    labelKey: "trustCenter.signals.iso27001.label",
    descKey: "trustCenter.signals.iso27001.desc",
    status: "in-progress",
    targetDate: "Q4 2026",
    icon: "file-check",
    badgeImage: "/badges/iso27001.svg",
  },
  {
    id: "hipaa",
    labelKey: "trustCenter.signals.hipaa.label",
    descKey: "trustCenter.signals.hipaa.desc",
    status: "in-progress",
    targetDate: "Roadmap",
    icon: "clock",
    badgeImage: "/badges/hipaa.svg",
  },
  {
    id: "pci-dss",
    labelKey: "trustCenter.signals.pci.label",
    descKey: "trustCenter.signals.pci.desc",
    status: "in-progress",
    targetDate: "Roadmap",
    icon: "file-check",
    badgeImage: "/badges/pci-dss.svg",
  },
];

// Tier 3 — framework aligned (controls mapped, not certified)
export const tier3Signals: TrustSignal[] = [
  {
    id: "nist-csf",
    labelKey: "trustCenter.signals.nistCsf.label",
    descKey: "trustCenter.signals.nistCsf.desc",
    status: "aligned",
    icon: "shield-check",
    badgeImage: "/badges/nist-csf.svg",
  },
  {
    id: "dora",
    labelKey: "trustCenter.signals.dora.label",
    descKey: "trustCenter.signals.dora.desc",
    status: "aligned",
    icon: "globe",
    badgeImage: "/badges/dora.svg",
  },
  {
    id: "nis2",
    labelKey: "trustCenter.signals.nis2.label",
    descKey: "trustCenter.signals.nis2.desc",
    status: "aligned",
    icon: "globe",
    badgeImage: "/badges/nis2.svg",
  },
  {
    id: "ccpa",
    labelKey: "trustCenter.signals.ccpa.label",
    descKey: "trustCenter.signals.ccpa.desc",
    status: "aligned",
    icon: "shield-check",
    badgeImage: "/badges/ccpa.svg",
  },
];

export interface Subprocessor {
  name: string;
  purpose: string;
  region: string;
  url: string;
}

export const subprocessors: Subprocessor[] = [
  { name: "Amazon Web Services", purpose: "Primary cloud infrastructure", region: "US, EU", url: "https://aws.amazon.com/compliance/" },
  { name: "Supabase", purpose: "Application database & auth", region: "US, EU", url: "https://supabase.com/security" },
  { name: "Stripe", purpose: "Payment processing", region: "Global", url: "https://stripe.com/privacy" },
  { name: "Resend", purpose: "Transactional email", region: "US", url: "https://resend.com/legal/privacy-policy" },
  { name: "PostHog", purpose: "Product analytics (consent-gated)", region: "EU", url: "https://posthog.com/privacy" },
  { name: "HubSpot", purpose: "Marketing CRM (consent-gated)", region: "US, EU", url: "https://legal.hubspot.com/privacy-policy" },
];

export const SECURITY_EMAIL = "security@csquaregrc.com";
